/**
 * AdminDisplayOrders — the order a dealer's listing shows its app photos in.
 *
 * Photographers SHOOT in walking order; the listing SHOWS a display order. Orders are named
 * and dealers are assigned one on the Dealers page (no assignment = the default). An order is
 * a list of shots plus three blocks:
 *   Damage photos — where damage shows. Damage only publishes when the dealer's "Show damage
 *                   photos" switch is on AND its order has this row (Randall's two-key rule).
 *   Extra photos  — free-mode, "+" and duplicated photos, in the order they were taken.
 *   Dealer photos — the dealer's own feed photos.
 * Saving re-sorts every affected car right away (hand-arranged cars are left alone).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../config';

const BLOCK_LABEL = { damage: 'Damage photos', extras: 'Extra photos (free-mode, "+", copies)', feed: "Dealer's own photos" };

export default function AdminDisplayOrders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [sel, setSel] = useState(null);        // full order from GET /{id}
  const [items, setItems] = useState([]);      // working copy of sel.items
  const [name, setName] = useState('');
  const [addCode, setAddCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [resetVin, setResetVin] = useState('');

  const call = useCallback(async (path, opts = {}) => {
    const token = localStorage.getItem('adminAuth');
    if (!token) { navigate('/admin/login'); throw new Error('Not signed in'); }
    const r = await fetch(`${API_BASE}/admin/display-orders${path}`, {
      ...opts, headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    });
    if (r.status === 401 || r.status === 403) {
      localStorage.removeItem('adminAuth'); navigate('/admin/login'); throw new Error('Session expired');
    }
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.detail || `Request failed (${r.status})`);
    return d;
  }, [navigate]);

  const loadList = useCallback(() => call('').then(setOrders).catch(e => setMsg({ err: e.message })), [call]);

  const open = useCallback(async (id) => {
    try {
      const o = await call(`/${id}`);
      setSel(o); setItems(o.items.map(({ kind, shot_code, shot_name }) => ({ kind, shot_code, shot_name })));
      setName(o.name); setAddCode('');
    } catch (e) { setMsg({ err: e.message }); }
  }, [call]);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => {
    if (!sel && orders.length) open((orders.find(o => o.is_default) || orders[0]).id);
  }, [orders, sel, open]);

  const dirty = sel && (name !== sel.name ||
    JSON.stringify(items.map(i => [i.kind, i.shot_code || null])) !==
    JSON.stringify(sel.items.map(i => [i.kind, i.shot_code || null])));

  const move = (i, d) => setItems(xs => {
    const j = i + d; if (j < 0 || j >= xs.length) return xs;
    const n = [...xs]; [n[i], n[j]] = [n[j], n[i]]; return n;
  });
  const remove = (i) => setItems(xs => xs.filter((_, k) => k !== i));
  const hasDamage = items.some(i => i.kind === 'damage');
  const addDamage = () => setItems(xs => {
    const at = xs.findIndex(x => x.kind === 'extras');   // just before the extras by default
    const n = [...xs]; n.splice(at < 0 ? n.length : at, 0, { kind: 'damage', shot_code: null }); return n;
  });
  const available = sel ? sel.available_shots.filter(s => !items.some(i => i.shot_code === s.shot_code)) : [];
  const addShot = () => {
    const s = available.find(a => a.shot_code === addCode); if (!s) return;
    setItems(xs => {
      const at = xs.findIndex(x => x.kind !== 'shot');      // after the last shot row
      const n = [...xs]; n.splice(at < 0 ? n.length : at, 0, { kind: 'shot', shot_code: s.shot_code, shot_name: s.shot_name });
      return n;
    });
    setAddCode('');
  };

  const failedNote = (r) => (r && r.failed && r.failed.length
    ? ` ${r.failed.length} car(s) could not be re-sorted (save again or Reset them): ${r.failed.join(', ')}.` : '');

  const save = async () => {
    const allow = sel.dealers.filter(d => d.show_damage_photos).length;
    if (sel.has_damage && !hasDamage && allow
        && !window.confirm(`Removing the damage row hides damage photos for ${allow} dealer(s) that allow them. Save anyway?`)) return;
    if (!sel.has_damage && hasDamage && allow
        && !window.confirm(`Adding a damage row STARTS publishing damage photos for ${allow} dealer(s) that allow them. Save anyway?`)) return;
    if (!window.confirm(`Save and re-sort the listings of all ${sel.dealers.length} dealer(s) using "${name}"?`)) return;
    setBusy(true); setMsg(null);
    try {
      const o = await call(`/${sel.id}`, { method: 'PUT', body: JSON.stringify({
        name, items: items.map(({ kind, shot_code }) => ({ kind, shot_code: kind === 'shot' ? shot_code : null })) }) });
      setSel(o); setItems(o.items.map(({ kind, shot_code, shot_name }) => ({ kind, shot_code, shot_name }))); setName(o.name);
      setMsg({ ok: `Saved. ${o.resort.reordered} of ${o.resort.cars} car(s) re-sorted`
        + (o.resort.arranged_skipped ? `, ${o.resort.arranged_skipped} hand-arranged left alone` : '') + '.'
        + (o.warning ? ' ' + o.warning : '') + failedNote(o.resort) });
      loadList();
    } catch (e) { setMsg({ err: e.message }); } finally { setBusy(false); }
  };

  const copy = async () => {
    const n = window.prompt(`Name for the new order (a copy of "${sel.name}"):`, `${sel.name} copy`);
    if (!n) return;
    setBusy(true); setMsg(null);
    try { const o = await call('', { method: 'POST', body: JSON.stringify({ name: n, copy_from: sel.id }) });
      await loadList(); open(o.id); setMsg({ ok: `Created "${o.name}". Assign it to dealers on the Dealers page.` });
    } catch (e) { setMsg({ err: e.message }); } finally { setBusy(false); }
  };

  const makeDefault = async () => {
    const row = orders.find(o => o.id === sel.id);
    const pub = sel.has_damage && row ? row.unassigned_damage_allowed : 0;
    if (!window.confirm(`Make "${sel.name}" the default? Every dealer without its own order switches to it and is re-sorted.`
        + (pub ? ` This STARTS publishing damage photos for ${pub} dealer(s) that allow them.` : ''))) return;
    setBusy(true); setMsg(null);
    try { const o = await call(`/${sel.id}/default`, { method: 'POST' }); setSel(o);
      setMsg({ ok: `Default changed. ${o.resort.reordered} of ${o.resort.cars} car(s) re-sorted.` + failedNote(o.resort) }); loadList();
    } catch (e) { setMsg({ err: e.message }); } finally { setBusy(false); }
  };

  const del = async () => {
    if (!window.confirm(`Delete "${sel.name}"?`)) return;
    try { await call(`/${sel.id}`, { method: 'DELETE' }); setSel(null); loadList(); setMsg({ ok: 'Deleted.' }); }
    catch (e) { setMsg({ err: e.message }); }
  };

  const resetCar = async () => {
    const vin = resetVin.trim().toUpperCase(); if (!vin) return;
    if (!window.confirm(`Put ${vin}'s photos back in its dealer's display order? Any hand arrangement is lost.`)) return;
    try { const r = await call('/cars/reset', { method: 'POST', body: JSON.stringify({ vin }) });
      setMsg({ ok: `${r.vin} reset to its dealer's order.` }); setResetVin('');
    } catch (e) { setMsg({ err: e.message }); }
  };

  let shotNo = 0;
  return (
    <div style={{ padding: 20, maxWidth: 1200, margin: '0 auto' }}>
      <h2 style={{ marginBottom: 4 }}>Display Orders</h2>
      <p style={{ color: '#6b7280', marginTop: 0, fontSize: 14 }}>
        The order a dealer's listing shows its app photos in. Photographers still shoot in walking order.
        Assign an order to a dealer on the <b>Dealers</b> page; dealers without one use the default.
      </p>
      {msg && <div style={{ color: msg.err ? '#dc2626' : '#059669', marginBottom: 10, fontWeight: 600 }}>
        {msg.err || msg.ok} <button onClick={() => setMsg(null)} style={linkBtn}>dismiss</button></div>}

      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ ...card, width: 280 }}>
          {orders.map(o => (
            <div key={o.id} onClick={() => { if (dirty && !window.confirm('Discard unsaved changes?')) return; open(o.id); }}
                 style={{ padding: '8px 10px', borderRadius: 6, cursor: 'pointer', marginBottom: 4,
                          background: sel && sel.id === o.id ? '#eaf3fd' : 'transparent' }}>
              <div style={{ fontWeight: 600 }}>{o.name} {o.is_default && <span style={badge}>default</span>}</div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>
                {o.shots} shots · {o.dealers} dealer(s) · damage {o.has_damage ? 'placed' : 'not placed'}</div>
            </div>
          ))}
          <div style={{ borderTop: '1px solid #eef2f7', marginTop: 10, paddingTop: 10, fontSize: 12, color: '#6b7280' }}>
            A car someone arranged by hand ("Make 1st", Arrange) keeps that order. To undo it:
            <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
              <input value={resetVin} onChange={e => setResetVin(e.target.value)} placeholder="VIN" style={{ ...inp, width: 150 }} />
              <button onClick={resetCar} disabled={!resetVin.trim()} style={btn}>Reset</button>
            </div>
          </div>
        </div>

        {sel && (
          <div style={{ ...card, flex: 1, minWidth: 420 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <input value={name} onChange={e => setName(e.target.value)} style={{ ...inp, width: 260, fontWeight: 600 }} />
              {sel.is_default ? <span style={badge}>default</span> : <button onClick={makeDefault} disabled={busy} style={btn}>Make default</button>}
              <button onClick={copy} disabled={busy} style={btn}>Copy as new order</button>
              {!sel.is_default && <button onClick={del} disabled={busy} style={{ ...linkBtn, color: '#dc2626' }}>Delete</button>}
              <div style={{ flex: 1 }} />
              <button onClick={save} disabled={busy || !dirty} style={{ ...primaryBtn, opacity: busy || !dirty ? 0.5 : 1 }}>
                {busy ? 'Saving…' : 'Save'}</button>
            </div>

            <div style={{ fontSize: 13, color: '#6b7280', margin: '10px 0' }}>
              Used by {sel.dealers.length} dealer(s){sel.dealers.length ? ': ' : ''}
              {sel.dealers.slice(0, 8).map(d => d.dealership_name).join(', ')}{sel.dealers.length > 8 ? '…' : ''}
            </div>
            {sel.missing_shots.length > 0 && (
              <div style={warnBox}>Not placed (these publish with the extras until added):{' '}
                {sel.missing_shots.map(s => s.shot_name).join(', ')}</div>)}
            {!hasDamage && (
              <div style={infoBox}>No damage row: damage photos stay hidden for every dealer on this order,
                even with Show damage photos on. <button onClick={addDamage} style={linkBtn}>+ Add damage position</button></div>)}

            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, marginTop: 8 }}>
              <tbody>
                {items.map((it, i) => {
                  const isShot = it.kind === 'shot'; if (isShot) shotNo += 1;
                  return (
                    <tr key={`${it.kind}-${it.shot_code || i}`} style={{ borderBottom: '1px solid #eef2f7',
                        background: isShot ? 'transparent' : '#f8fafc' }}>
                      <td style={{ ...td, width: 40, color: '#94a3b8' }}>{isShot ? shotNo : ''}</td>
                      <td style={td}>{isShot ? (it.shot_name || it.shot_code)
                        : <b>{BLOCK_LABEL[it.kind]}</b>}{i === 0 && <span style={{ ...badge, marginLeft: 8 }}>main photo</span>}</td>
                      <td style={{ ...td, width: 130, whiteSpace: 'nowrap', textAlign: 'right' }}>
                        <button onClick={() => move(i, -1)} disabled={i === 0} style={moveBtn} title="Move up">▲</button>
                        <button onClick={() => move(i, 1)} disabled={i === items.length - 1} style={moveBtn} title="Move down">▼</button>
                        {(isShot || it.kind === 'damage') &&
                          <button onClick={() => remove(i)} style={{ ...moveBtn, color: '#dc2626' }} title="Remove">✕</button>}
                      </td>
                    </tr>);
                })}
              </tbody>
            </table>

            {available.length > 0 && (
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <select value={addCode} onChange={e => setAddCode(e.target.value)} style={inp}>
                  <option value="">Add a shot…</option>
                  {available.map(s => <option key={s.shot_code} value={s.shot_code}>{s.shot_name}</option>)}
                </select>
                <button onClick={addShot} disabled={!addCode} style={btn}>Add</button>
              </div>)}
          </div>
        )}
      </div>
    </div>
  );
}

const card = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 14 };
const btn = { padding: '7px 12px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', cursor: 'pointer' };
const primaryBtn = { ...btn, background: '#0078D4', color: '#fff', border: 'none', fontWeight: 600 };
const linkBtn = { background: 'none', border: 'none', color: '#0078D4', cursor: 'pointer', padding: 0 };
const moveBtn = { ...btn, padding: '2px 8px', marginLeft: 4 };
const inp = { padding: 7, border: '1px solid #d1d5db', borderRadius: 6 };
const td = { padding: '6px 8px' };
const badge = { fontSize: 11, background: '#e0f2fe', color: '#0369a1', borderRadius: 10, padding: '1px 8px', fontWeight: 600 };
const warnBox = { background: '#fff7ed', border: '1px solid #fed7aa', color: '#9a3412', borderRadius: 6, padding: '8px 10px', fontSize: 13, marginBottom: 8 };
const infoBox = { background: '#f0f7ff', border: '1px solid #cfe3ff', color: '#345', borderRadius: 6, padding: '8px 10px', fontSize: 13, marginBottom: 8 };
